/* eslint-disable @typescript-eslint/no-unused-vars */
import type { StepConfig, StepperState, ValidationResult } from '@/design-system/Organisms/AdvanceStepper/types';
import { useCallback, useEffect, useReducer, useRef } from 'react';

type StepperAction<TData> =
    | { type: 'SET_CURRENT_STEP'; payload: number }
    | { type: 'SET_LOADING'; payload: boolean }
    | { type: 'SET_DATA'; payload: Partial<TData> }
    | { type: 'SET_ERRORS'; payload: { stepId: string; errors: string[] } }
    | { type: 'CLEAR_ERRORS'; payload: string }
    | { type: 'COMPLETE_STEP'; payload: string }
    | { type: 'SKIP_STEP'; payload: string }
    | { type: 'RESET' }
    | { type: 'UPDATE_NAVIGATION_STATE'; payload: { canGoNext: boolean; canGoPrevious: boolean } };

function stepperReducer<TData>(
    state: StepperState<TData>,
    action: StepperAction<TData>
): StepperState<TData> {
    switch (action.type) {
        case 'SET_CURRENT_STEP':
            return { ...state, currentStepIndex: action.payload };

        case 'SET_LOADING':
            return { ...state, isLoading: action.payload };

        case 'SET_DATA':
            return {
                ...state,
                stepData: { ...state.stepData, ...action.payload }
            };

        case 'SET_ERRORS':
            return {
                ...state,
                errors: {
                    ...state.errors,
                    [action.payload.stepId]: action.payload.errors
                }
            };

        case 'CLEAR_ERRORS':
            {
                const { [action.payload]: _, ...restErrors } = state.errors;
                return { ...state, errors: restErrors };
            }

        case 'COMPLETE_STEP':
            return {
                ...state,
                completedSteps: new Set([...state.completedSteps, action.payload])
            };

        case 'SKIP_STEP':
            return {
                ...state,
                skippedSteps: new Set([...state.skippedSteps, action.payload])
            };

        case 'UPDATE_NAVIGATION_STATE':
            return {
                ...state,
                canGoNext: action.payload.canGoNext,
                canGoPrevious: action.payload.canGoPrevious
            };

        case 'RESET':
            return {
                currentStepIndex: 0,
                completedSteps: new Set(),
                skippedSteps: new Set<string>(),
                stepData: {} as TData,
                isLoading: false,
                errors: {},
                canGoNext: true,
                canGoPrevious: false
            };

        default:
            return state;
    }
}

interface UseAdvancedStepperOptions<TData> {
    autoSave?: boolean;
    persistKey?: string;
    onComplete?: (data: TData) => Promise<void> | void;
    onStepChange?: (fromStep: string | null, toStep: string, data: TData) => void;
    onError?: (error: Error, step: string) => void;
}

export function useAdvancedStepper<TData = any>(
    steps: StepConfig<TData>[],
    initialData: TData,
    options: UseAdvancedStepperOptions<TData> = {}
) {
    const { autoSave = false, persistKey, onComplete, onStepChange, onError } = options;

    const initialState: StepperState<TData> = {
        currentStepIndex: 0,
        completedSteps: new Set<string>(),
        skippedSteps: new Set<string>(),
        stepData: initialData,
        isLoading: false,
        errors: {},
        canGoNext: true,
        canGoPrevious: false
    };

    const [state, dispatch] = useReducer(stepperReducer<TData>, initialState);

    const persistTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        if (persistKey && typeof window !== 'undefined') {
            try {
                const saved = localStorage.getItem(persistKey);
                if (saved) {
                    const parsedData = JSON.parse(saved);
                    dispatch({ type: 'SET_DATA', payload: parsedData });
                }
            } catch (e) {
                console.warn('Failed to load persisted stepper data:', e);
            }
        }
    }, [persistKey]);

    useEffect(() => {
        if (persistKey && autoSave && typeof window !== 'undefined') {
            if (persistTimeoutRef.current !== null) {
                clearTimeout(persistTimeoutRef.current);
            }
            persistTimeoutRef.current = setTimeout(() => {
                try {
                    localStorage.setItem(persistKey, JSON.stringify(state.stepData));
                } catch (e) {
                    console.warn('Failed to persist stepper data:', e);
                }
            }, 500);
        }
    }, [state.stepData, persistKey, autoSave]);

    useEffect(() => {
        const canGoNext = state.currentStepIndex < steps.length - 1;
        const canGoPrevious = state.currentStepIndex > 0;

        dispatch({
            type: 'UPDATE_NAVIGATION_STATE',
            payload: { canGoNext, canGoPrevious }
        });
    }, [state.currentStepIndex, steps.length]);

    const currentStep = steps[state.currentStepIndex] || null;

    const validateStep = useCallback(async (stepId: string, data: TData): Promise<ValidationResult> => {
        const step = steps.find(s => s.id === stepId);
        if (!step?.validation) {
            return { isValid: true };
        }

        try {
            const result = await step.validation(data);
            return result;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Validation failed';
            onError?.(error instanceof Error ? error : new Error(errorMessage), stepId);
            return { isValid: false, errors: [errorMessage] };
        }
    }, [steps, onError]);

    const validateCurrentStep = useCallback(async (): Promise<ValidationResult> => {
        if (!currentStep) {
            return { isValid: false, errors: ['No current step'] };
        }

        dispatch({ type: 'SET_LOADING', payload: true });

        try {
            const result = await validateStep(currentStep.id, state.stepData);

            if (!result.isValid && result.errors) {
                dispatch({
                    type: 'SET_ERRORS',
                    payload: { stepId: currentStep.id, errors: result.errors }
                });
            } else {
                dispatch({ type: 'CLEAR_ERRORS', payload: currentStep.id });
            }

            return result;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Validation failed';
            dispatch({
                type: 'SET_ERRORS',
                payload: { stepId: currentStep.id, errors: [errorMessage] }
            });
            return { isValid: false, errors: [errorMessage] };
        } finally {
            dispatch({ type: 'SET_LOADING', payload: false });
        }
    }, [currentStep, state.stepData, validateStep]);

    const goToStep = useCallback(async (stepId: string): Promise<void> => {
        const targetIndex = steps.findIndex(step => step.id === stepId);
        if (targetIndex === -1) {
            console.warn(`Step with id "${stepId}" not found`);
            return;
        }

        const currentStepId = currentStep?.id || null;
        const targetStep = steps[targetIndex];

        try {
            if (currentStep?.onExit) {
                await currentStep.onExit(state.stepData);
            }

            dispatch({ type: 'SET_CURRENT_STEP', payload: targetIndex });

            if (targetStep?.onEnter) {
                const helpers = {
                    onDataChange: (updates: Partial<TData>) => {
                        dispatch({ type: 'SET_DATA', payload: updates });
                    }
                };
                await targetStep.onEnter(state.stepData, helpers);
            }

            if (stepId === 'review' && currentStepId === 'user-info') {
                const paymentStep = steps.find(s => s.id === 'payment');
                if (paymentStep?.shouldSkip?.(state.stepData)) {
                    dispatch({ type: 'COMPLETE_STEP', payload: 'payment' });
                }
            }

            if (onStepChange && currentStepId) {
                onStepChange(currentStepId, stepId, state.stepData);
            }
        } catch (error) {
            const err = error instanceof Error ? error : new Error('Failed to navigate to step');
            onError?.(err, stepId);
            console.error('Error navigating to step:', error);
        }
    }, [steps, currentStep, state.stepData, onStepChange, onError]);

    const goNext = useCallback(async (): Promise<void> => {
        if (!currentStep) return;

        if (currentStep.shouldSkip?.(state.stepData)) {
            dispatch({ type: 'SKIP_STEP', payload: currentStep.id });
            const nextIndex = state.currentStepIndex + 1;
            if (nextIndex < steps.length) {
                await goToStep(steps[nextIndex].id);
            }
            return;
        }

        const validation = await validateCurrentStep();
        if (!validation.isValid) {
            return;
        }

        dispatch({ type: 'COMPLETE_STEP', payload: currentStep.id });

        const nextIndex = state.currentStepIndex + 1;
        if (nextIndex < steps.length) {
            await goToStep(steps[nextIndex].id);
        } else if (onComplete) {
            try {
                await onComplete(state.stepData);
            } catch (error) {
                const err = error instanceof Error ? error : new Error('Failed to complete checkout');
                onError?.(err, 'completion');
                console.error('Error completing checkout:', error);
            }
        }
    }, [currentStep, state.currentStepIndex, state.stepData, validateCurrentStep, steps, goToStep, onComplete, onError]);

    const goPrevious = useCallback(async (): Promise<void> => {
        if (!state.canGoPrevious) return;

        const prevIndex = state.currentStepIndex - 1;
        if (prevIndex >= 0) {
            await goToStep(steps[prevIndex].id);
        }
    }, [state.currentStepIndex, state.canGoPrevious, steps, goToStep]);

    const skipStep = useCallback(async (): Promise<void> => {
        if (!currentStep?.isSkippable) return;

        dispatch({ type: 'SKIP_STEP', payload: currentStep.id });

        const nextIndex = state.currentStepIndex + 1;
        if (nextIndex < steps.length) {
            await goToStep(steps[nextIndex].id);
        }
    }, [currentStep, state.currentStepIndex, steps, goToStep]);

    const updateData = useCallback((updates: Partial<TData>): void => {
        dispatch({ type: 'SET_DATA', payload: updates });
    }, []);

    const reset = useCallback((): void => {
        dispatch({ type: 'RESET' });
        if (persistKey && typeof window !== 'undefined') {
            try {
                localStorage.removeItem(persistKey);
            } catch (e) {
                console.warn('Failed to clear persisted data:', e);
            }
        }
    }, [persistKey]);

    const getStepStatus = useCallback((stepId: string): 'pending' | 'current' | 'completed' | 'skipped' => {
        if (state.completedSteps.has(stepId)) return 'completed';
        if (state.skippedSteps.has(stepId)) return 'skipped';
        if (currentStep?.id === stepId) return 'current';
        return 'pending';
    }, [state.completedSteps, state.skippedSteps, currentStep]);

    const getProgress = useCallback((): number => {
        const total = steps.length;
        const completed = state.completedSteps.size;
        return total === 0 ? 0 : completed / total;
    }, [steps.length, state.completedSteps]);

    const completeStep = useCallback((stepId: string): void => {
        dispatch({ type: 'COMPLETE_STEP', payload: stepId });
    }, []);

    useEffect(() => {
        return () => {
            if (persistTimeoutRef.current) {
                clearTimeout(persistTimeoutRef.current);
            }
        };
    }, []);

    return {
        currentStep,
        currentStepIndex: state.currentStepIndex,
        stepData: state.stepData,
        isLoading: state.isLoading,
        errors: state.errors,
        canGoNext: state.canGoNext,
        canGoPrevious: state.canGoPrevious,
        completedSteps: state.completedSteps,
        skippedSteps: state.skippedSteps,

        goToStep,
        goNext,
        goPrevious,
        skipStep,
        updateData,
        validateCurrentStep,
        validateStep,
        reset,
        completeStep,

        getStepStatus,
        getProgress,

        steps,
        totalSteps: steps.length,
        isFirstStep: state.currentStepIndex === 0,
        isLastStep: state.currentStepIndex === steps.length - 1,
    };
}